import { ChangeEvent, FunctionComponent, useEffect, useState } from "react"
import { useTypedDispatch, useTypedSelector } from "../../../app/store"
import { Information } from "../../simulation/types"
import { setActiveChoice, setSelectedTreeItem } from "../decisionsSlice"
import { ChoiceType } from "../types"

type CustomDecisionProps = {
  onDecisionMade: (decision?: Information) => void
}

const genActionSentence = (decision: string) => {
  return `Your decision to "${decision}" is being implemented by your team. You will receive an update shortly.`
}

const genPlanSentence = (decision: string) => {
  return `Your plan to "${decision}" is ready to be implemented by your team.`
}

const CustomDecision: FunctionComponent<CustomDecisionProps> = (props) => {
  const dispatch = useTypedDispatch()
  const [decision, setDecision] = useState("")
  const [debouncedDecision, setDebouncedDecision] = useState("")
  const [decisionExplanation, setDecisionExplanation] = useState("")
  const [decisionLengthOk, setDecisionLengthOk] = useState(false)
  const maxDecisionLength = 35
  const maxExplanationLength = 250
  const minDecisionLength = 10

  const activeChoiceType = useTypedSelector(
    (state) => state.decisionsSlice.activeChoiceType,
  )

  const { decisions, plans } = useTypedSelector(
    (state) => state.simulationSlice,
  )

  const resetDecisionState = () => {
    setDecision("")
    setDecisionExplanation("")
    setActiveChoice(null)
  }

  const setCustomActiveChoice = () => {
    if (activeChoiceType === ChoiceType.search) return
    if (decision.length <= minDecisionLength) return

    const selectedTreeItem = {
      id: -1,
      text: decision,
      icon: null,
      order: -1,
      actionSentence: genActionSentence(decision),
      planSentence: genPlanSentence(decision),
      isInfoSearch: 0,
      infoSearchCategory: null,
      measureReference: null,
      categories: [],
    } as Information

    dispatch(setSelectedTreeItem(selectedTreeItem))
  }

  const handleDecisionChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.target.value.length >= minDecisionLength
      ? setDecisionLengthOk(true)
      : setDecisionLengthOk(false)
    setDecision(event.target.value)
  }

  const handleDecisionExplanationChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const explanation = event.target.value
    if (explanation.length <= maxExplanationLength) {
      setDecisionExplanation(explanation)
    }
    setCustomActiveChoice()
  }

  useEffect(() => {
    // monitor decisions and plans to see if ours is there
    const decisionExists = decisions.find(
      (d) => d.item.actionSentence === genActionSentence(decision),
    )
    const planExists = plans.find(
      (p) => p.item.planSentence === genPlanSentence(decision),
    )
    if (decisionExists || planExists) {
      props.onDecisionMade()
      resetDecisionState()
    }
  }, [decisions, plans])

  // Watch for the debounced decision to change
  useEffect(() => {
    setCustomActiveChoice()
  }, [debouncedDecision])

  // Debounce the decision input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedDecision(decision)
    }, 100)
    return () => clearTimeout(timeoutId)
  }, [decision, 100])

  return (
    <div id="customDecisionContainer" style={{ pointerEvents: "auto" }}>
      <p className="text-gray-700 mb-4">
        No results found. We suggest you try an alternate search term, but if
        you would like to proceed with a custom decision, please provide context
        below.
      </p>
      <div className="flex flex-col space-y-4">
        <div>
          <div className="w-full relative rounded-lg bg-base-white shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] box-border overflow-hidden flex flex-row items-center justify-start py-2.5 px-3.5 text-left text-base text-gray-500 font-text-md-regular border-[1px] border-solid border-gray-300">
            <input
              id="customDecisionInput"
              type="text"
              value={decision}
              maxLength={maxDecisionLength}
              onChange={handleDecisionChange}
              onBlur={handleDecisionChange}
              placeholder="Custom Decision"
              className="w-full bg-transparent outline-none placeholder-gray-500 text-gray-700 pl-2.5 pr-3.5 leading-[24px] font-text-md-regular"
            />
          </div>
          <p
            className={`text-gray-500 text-xs text-right !mb-0 ${
              decisionLengthOk || "text-red-500"
            }`}>
            At least {minDecisionLength} characters
          </p>
        </div>
        <div>
          <div className="w-full relative rounded-lg bg-base-white shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] box-border overflow-hidden flex flex-row items-center justify-start py-2.5 px-3.5 text-left text-base text-gray-500 font-text-md-regular border-[1px] border-solid border-gray-300">
            <input
              id="customDecisionExplanationInput"
              type="text"
              value={decisionExplanation}
              onChange={handleDecisionExplanationChange}
              placeholder="Custom Decision Explanation"
              maxLength={maxExplanationLength}
              className="w-full bg-transparent outline-none placeholder-gray-500 text-gray-700 pl-2.5 pr-3.5 leading-[24px] font-text-md-regular"
            />
          </div>
          <p className="text-gray-500 text-xs text-right !mb-0">
            {maxExplanationLength - decisionExplanation.length} characters
            remaining
          </p>
        </div>
      </div>
    </div>
  )
}

export default CustomDecision
